/* eslint-disable camelcase, jsx-a11y/anchor-is-valid */
import React, { useMemo } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import cn from "classnames";
import { useAppState } from "hooks/AppContext";
import { Culture } from "interfaces/api/apiInterfaces";
import { PathParts, getPathParts, setQueryParam } from "utils/url";
import styles from "./CulturesNav.module.scss";

type CultureLinkProps = {
    activeCulture: Culture | null;
    culture: Culture;
    location: PathParts;
};

const CultureLink = ({ activeCulture, culture, location }: CultureLinkProps) => {
    const { code, name } = culture;
    const { pathname, query } = location;
    const linkQuery = setQueryParam({ key: "culture", query, value: code });
    const active = (!code && !activeCulture?.code) || (code === activeCulture?.code);

    return (
        <Link href={{ pathname, query: linkQuery }}>
            <a
                aria-current={active}
                className={cn(styles.link, {
                    [styles["link--active"]]: active,
                })}
                data-testid={`cultures-nav__${code || "main"}`}
            >
                {name}
            </a>
        </Link>
    );
};

export const CulturesNav = () => {
    const router = useRouter();
    const { cultures } = useAppState();
    const location = useMemo<PathParts>(() => getPathParts(router.asPath), [router.asPath]);
    const activeCulture = useMemo<Culture | null>(() => {
        if (!cultures) {
            return null;
        }
        const { query } = getPathParts(router.asPath);
        const locationCulture = new URLSearchParams(query).get("culture");
        return cultures.find(({ code }) => code === locationCulture) || null;
    }, [cultures, router.asPath]);

    return !!cultures?.length && (
        <nav className={styles.nav} aria-label="Filter by Culture" data-testid="cultures-nav">
            <CultureLink
                activeCulture={activeCulture}
                culture={{ id: "main", name: "All Content" }}
                location={location}
            />
            {cultures?.map((culture) => (
                <CultureLink
                    activeCulture={activeCulture}
                    culture={culture}
                    location={location}
                    key={culture.id}
                />
            ))}
        </nav>
    );
};
